import { useEffect, useRef, useState } from "react";
import {
    Sparkles, Send, Loader2, StickyNote, Mail, MessageSquare, Inbox,
    ShieldQuestion, X, Copy, CheckCircle2, Check, Phone, Video, Volume2, Square,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import DOMPurify from "dompurify";

// KI-Coach & Chat-Timeline pro Lead (Jun-2026, User-Wunsch):
// Einschätzung automatisch beim Öffnen, Einwandsbehandlung mit versandfertigem
// E-Mail-Entwurf (direkt versendbar) und fortlaufende Chat-Dokumentation
// (Notizen, SMS/WhatsApp, E-Mails, KI-Hinweise).

const QUICK_OBJECTIONS = ["Zu teuer", "Muss noch überlegen", "Woanders günstiger", "Meldet sich nicht mehr"];

const fmtAt = (at) => {
    if (!at) return "";
    const d = new Date(at);
    return isNaN(d) ? "" : d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

const Bold = ({ text }) => (text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : <span key={i}>{p}</span>);

const EVENT_STYLE = {
    note: { side: "right", cls: "bg-[#FFCC00]/25 border-[#FFCC00]/60 text-[#111827]", icon: StickyNote, label: "Notiz" },
    sms_in: { side: "left", cls: "bg-white border-gray-200 text-gray-800", icon: MessageSquare, label: "Kunde" },
    sms_out: { side: "right", cls: "bg-white border-gray-300 text-gray-800", icon: MessageSquare, label: "SMS/WhatsApp" },
    email_out: { side: "right", cls: "bg-white border-gray-300 text-gray-800", icon: Mail, label: "E-Mail" },
    outcome: { side: "right", cls: "bg-blue-50 border-blue-300 text-blue-900", icon: Phone, label: "Ergebnis" },
    call: { side: "right", cls: "bg-blue-50 border-blue-300 text-blue-900", icon: Phone, label: "Anruf/Kontakt" },
    assign: { side: "right", cls: "bg-gray-50 border-gray-300 text-gray-800", icon: Check, label: "Zuweisung" },
    video: { side: "right", cls: "bg-emerald-50 border-emerald-300 text-emerald-900", icon: Video, label: "Video" },
    wishes: { side: "left", cls: "bg-[#FFFBEB] border-[#FFCC00]/70 text-gray-800", icon: ShieldQuestion, label: "Kundenwünsche & Fragen" },
    ai: { side: "left", cls: "bg-[#111827] border-[#111827] text-white", icon: Sparkles, label: "KI-Coach" },
};

function TimelineEvent({ ev }) {
    if (ev.type === "created") {
        return (
            <div className="text-center text-[11px] text-gray-500 px-4 py-1">
                <span className="inline-flex items-center gap-1 font-bold uppercase tracking-wider text-[10px]"><Inbox className="w-3 h-3" /> {fmtAt(ev.at)} · {ev.author}</span>
                <div className="whitespace-pre-wrap mt-0.5 max-h-20 overflow-hidden">{ev.text}</div>
            </div>
        );
    }
    const s = EVENT_STYLE[ev.type] || EVENT_STYLE.note;
    const Icon = s.icon;
    return (
        <div className={`flex ${s.side === "right" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[88%] rounded-sm border px-3 py-2 ${s.cls}`}>
                <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-0.5 ${ev.type === "ai" ? "text-[#FFCC00]" : "text-gray-500"}`}>
                    <Icon className="w-3 h-3" /> {s.label}{ev.author && ev.author !== s.label ? ` · ${ev.author}` : ""} · {fmtAt(ev.at)}
                </div>
                <div className="text-xs leading-relaxed whitespace-pre-wrap"><Bold text={ev.text} /></div>
            </div>
        </div>
    );
}

// Arbeitsablauf-Stepper (Jun-2026, User-Wunsch): klare Reihenfolge der Lead-Bearbeitung.
// Aug-2026 (User-Wunsch): eigenständig in der Lead-Karte nutzbar — mit `before`
// (KI-Coach-Knopf vor Schritt 1) und `actions` (kleine Aktions-Buttons darunter).
export function LeadWorkflow({ lead, items, before, actions }) {
    const hasAdvice = !!(lead?.notes) || (items || []).some((e) => e.type === "ai" || e.type === "note");
    const steps = [
        { label: "1. Kontaktieren", done: !!(lead?.contact_attempts || lead?.outcome || lead?.first_contact_at), hint: "Anruf / WhatsApp / E-Mail — Ergebnis erfassen" },
        { label: "2. Beraten & Einwände", done: hasAdvice, hint: "KI-Coach nutzen, Notizen dokumentieren" },
        { label: "3. Termin / Wiedervorlage", done: !!lead?.follow_up_date, hint: "Probefahrt oder Wiedervorlage setzen" },
        { label: "4. Angebot / Umwandeln", done: !!lead?.converted_listing_id, hint: "In Kauf/Bestellung umwandeln" },
        { label: "5. Abschluss", done: lead?.status === "won", hint: "Lead als gewonnen abschließen" },
    ];
    const currentIdx = steps.findIndex((s) => !s.done);
    return (
        <div className="md:col-span-2 border border-[#111827]/15 bg-white rounded-sm px-3 py-2.5" data-testid={`lead-workflow-${lead?.id || ""}`}>
            {/* Aug-2026 (User-Wunsch) — alle Schritte in EINER Zeile (horizontal scrollbar) */}
            <div className="flex flex-nowrap items-center overflow-x-auto pb-0.5">
                {before && <div className="flex items-center mr-2 shrink-0">{before}</div>}
                {steps.map((s, i) => (
                    <div key={s.label} className="flex items-center shrink-0">
                        <div className={`flex items-center gap-1.5 whitespace-nowrap rounded-sm px-2 py-1 text-[11px] font-bold border ${
                            s.done ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                : i === currentIdx ? "bg-[#111827] border-[#111827] text-[#FFCC00]"
                                    : "bg-gray-50 border-gray-200 text-gray-400"}`}
                            title={s.hint} data-testid={`lead-workflow-step-${i}`}>
                            {s.done ? <Check className="w-3 h-3" /> : <span className="w-3 h-3 inline-flex items-center justify-center text-[9px]">{i + 1}</span>}
                            {s.label.replace(/^\d+\. /, "")}
                        </div>
                        {i < steps.length - 1 && <span className={`mx-1 h-px w-4 shrink-0 ${s.done ? "bg-emerald-300" : "bg-gray-200"}`} />}
                    </div>
                ))}
            </div>
            {currentIdx >= 0 && (
                <div className="mt-1 text-[11px] text-gray-500 text-left" data-testid="lead-workflow-next">
                    <b className="text-[#111827]">Nächster Schritt:</b> {steps[currentIdx].hint}
                </div>
            )}
            {currentIdx === -1 && (
                <div className="mt-1 text-[11px] font-bold text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Alle Schritte erledigt</div>
            )}
            {actions}
        </div>
    );
}

// Aug-2026 (User-Wunsch) — Ursprungsmail als Klartext MIT Bildern/Grafiken statt Roh-HTML:
// lädt die Original-E-Mail (body_html) und rendert sie sicher (DOMPurify-sanitisiert).
const HTML_RE = /<\s*(html|body|div|table|p|br|img|span|a|font)[\s>/]/i;
function OriginalEmailBody({ leadId, lead }) {
    const [html, setHtml] = useState(undefined);
    useEffect(() => {
        let alive = true;
        api.get(`/dealer/leads/${leadId}/source-email`)
            .then((r) => { if (alive) setHtml(r.data.body_html || null); })
            .catch(() => { if (alive) setHtml(null); });
        return () => { alive = false; };
    }, [leadId]);
    if (html === undefined) {
        return <div className="mt-1.5 text-[11px] text-gray-500 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> E-Mail wird geladen…</div>;
    }
    const src = html || (HTML_RE.test(lead?.raw_text || "") ? lead.raw_text : null);
    if (src) {
        // cid:-Inline-Anhänge können nicht geladen werden → entfernen statt Broken-Image
        const cleaned = src.replace(/<img[^>]+src=["']?cid:[^>]*>/gi, "");
        return (
            <div className="mt-1.5 max-h-72 overflow-auto bg-white border border-gray-200 rounded-sm p-2.5 text-[12px] text-gray-800 [&_img]:max-w-full [&_img]:h-auto [&_a]:text-blue-700 [&_a]:underline [&_table]:max-w-full"
                data-testid={`lead-original-email-html-${leadId}`}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cleaned, { FORBID_TAGS: ["script", "iframe", "form", "input"], ADD_ATTR: ["target"] }) }} />
        );
    }
    return (
        <pre className="mt-1.5 max-h-56 overflow-auto whitespace-pre-wrap bg-gray-50 border border-gray-200 rounded-sm p-2.5 text-[11px] text-gray-700 font-sans"
            data-testid={`lead-original-email-text-${leadId}`}>{lead?.raw_text}</pre>
    );
}

export default function LeadTimeline({ leadId, lead, footerActions }) {
    const [coachMsgs, setCoachMsgs] = useState(null);
    const [coachBusy, setCoachBusy] = useState(false);
    const [coachInput, setCoachInput] = useState("");
    const [items, setItems] = useState(null);
    const [note, setNote] = useState("");
    const [noteBusy, setNoteBusy] = useState(false);
    const [showObjection, setShowObjection] = useState(true);
    const [quickObjections, setQuickObjections] = useState(QUICK_OBJECTIONS);
    const [objText, setObjText] = useState("");
    const [objBusy, setObjBusy] = useState(false);
    const [draft, setDraft] = useState(null); // {subject, body}
    const [sending, setSending] = useState(false);
    const [followUp, setFollowUp] = useState(lead?.follow_up_date || "");
    const fuTimer = useRef(null);
    const coachRef = useRef(null);
    const tlRef = useRef(null);
    // Aug-2026 (User-Wunsch) — Coach-Einschätzung vorlesen (TTS)
    const [speaking, setSpeaking] = useState(null);
    const audioRef = useRef(null);
    useEffect(() => () => { audioRef.current?.pause(); }, []);

    const speak = async (text, idx) => {
        if (speaking === idx) { audioRef.current?.pause(); setSpeaking(null); return; }
        audioRef.current?.pause();
        setSpeaking(`busy-${idx}`);
        try {
            const r = await api.post(`/dealer/leads/${leadId}/assistant/speak`, { text }, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = new Audio(url);
            audioRef.current = a;
            a.onended = () => { setSpeaking(null); URL.revokeObjectURL(url); };
            setSpeaking(idx);
            await a.play();
        } catch { setSpeaking(null); toast.error("Vorlesen derzeit nicht möglich"); }
    };


    const loadTimeline = async () => {
        try {
            const r = await api.get(`/dealer/leads/${leadId}/timeline`);
            let evs = r.data.items || [];
            // Aug-2026 (User-Wunsch) — SMS/WhatsApp in den normalen Verlauf integrieren
            try {
                const sms = await api.get(`/dealer/leads/${leadId}/sms-thread`);
                const known = new Set(evs.filter((e) => String(e.type || "").startsWith("sms")).map((e) => `${(e.at || "").slice(0, 16)}|${e.text}`));
                const extra = (sms.data.items || [])
                    .map((m) => ({ type: m.direction === "in" ? "sms_in" : "sms_out", at: m.at, text: m.body || "", author: m.direction === "in" ? "Kunde" : (m.channel === "whatsapp" ? "WhatsApp" : "SMS") }))
                    .filter((e) => e.text && !known.has(`${(e.at || "").slice(0, 16)}|${e.text}`));
                if (extra.length) evs = [...evs, ...extra].sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
            } catch { /* SMS optional */ }
            setItems(evs);
        } catch { setItems([]); }
    };

    useEffect(() => {
        loadTimeline();
        // Aug-2026 (User-Wunsch) — Kundenwünsche/Fragen im Verlauf: falls noch
        // nicht extrahiert, jetzt nachziehen und Verlauf aktualisieren.
        if (lead?.raw_text && !Array.isArray(lead.customer_wishes)) {
            api.post(`/dealer/leads/${leadId}/wishes`).then((r) => {
                if ((r.data.wishes || []).length || (r.data.questions || []).length) loadTimeline();
            }).catch(() => {});
        }
        // Aug-2026 (User-Wunsch) — systemweit gelernte Top-Einwände als Schnellauswahl
        api.get("/dealer/leads/objections/top").then((r) => {
            const arr = (r.data.items || []).map((i) => i.objection).filter(Boolean);
            if (arr.length) setQuickObjections(arr.slice(0, 6));
        }).catch(() => {});
        // Automatische Erst-Einschätzung bei jedem Öffnen (User-Wunsch)
        (async () => {
            setCoachBusy(true);
            try {
                const r = await api.post(`/dealer/leads/${leadId}/assistant/chat`, { message: null });
                setCoachMsgs(r.data.messages || []);
            } catch (e) {
                toast.error(e?.response?.data?.detail || "KI-Coach nicht erreichbar");
                setCoachMsgs([]);
            }
            setCoachBusy(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leadId]);

    useEffect(() => { if (coachRef.current) coachRef.current.scrollTop = coachRef.current.scrollHeight; }, [coachMsgs, coachBusy]);
    useEffect(() => { if (tlRef.current) tlRef.current.scrollTop = tlRef.current.scrollHeight; }, [items]);

    const askCoach = async () => {
        const t = coachInput.trim();
        if (!t || coachBusy) return;
        setCoachInput("");
        setCoachMsgs((p) => [...(p || []), { role: "user", text: t, at: new Date().toISOString() }]);
        setCoachBusy(true);
        try {
            const r = await api.post(`/dealer/leads/${leadId}/assistant/chat`, { message: t });
            setCoachMsgs(r.data.messages || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "KI-Coach nicht erreichbar"); }
        setCoachBusy(false);
    };

    const runObjection = async (text) => {
        if (objBusy) return;
        setObjBusy(true);
        try {
            const r = await api.post(`/dealer/leads/${leadId}/assistant/objection`, { objection: text || null });
            setDraft({ subject: r.data.email_subject || "", body: r.data.email_body || "" });
            setItems((p) => [...(p || []), r.data.event]);
            setShowObjection(false);
            setObjText("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Einwandsbehandlung nicht erreichbar"); }
        setObjBusy(false);
    };

    const sendDraft = async () => {
        if (!draft?.subject.trim() || !draft?.body.trim() || sending) return;
        setSending(true);
        try {
            const r = await api.post(`/dealer/leads/${leadId}/send-email`, { subject: draft.subject.trim(), body: draft.body.trim() });
            setItems((p) => [...(p || []), r.data.event]);
            setDraft(null);
            toast.success("E-Mail an den Kunden versendet (bzw. zur Freigabe eingereicht)");
        } catch (e) { toast.error(e?.response?.data?.detail || "E-Mail konnte nicht gesendet werden"); }
        setSending(false);
    };

    const addNote = async () => {
        const t = note.trim();
        if (!t || noteBusy) return;
        setNoteBusy(true);
        try {
            const r = await api.post(`/dealer/leads/${leadId}/timeline/note`, { text: t });
            setItems((p) => [...(p || []), r.data.event]);
            setNote("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Notiz konnte nicht gespeichert werden"); }
        setNoteBusy(false);
    };

    // Aug-2026 (User-Wunsch) — Wiedervorlage direkt neben der Notiz-Eingabe
    const saveFollowUp = (v) => {
        setFollowUp(v);
        clearTimeout(fuTimer.current);
        fuTimer.current = setTimeout(async () => {
            try {
                await api.patch(`/dealer/leads/${leadId}/status`, { follow_up_date: v || "" });
                toast.success(v ? `Wiedervorlage gesetzt: ${v}` : "Wiedervorlage entfernt");
            } catch (e) { toast.error(e?.response?.data?.detail || "Wiedervorlage konnte nicht gespeichert werden"); }
        }, 600);
    };

    return (
        <div className="mt-3 grid md:grid-cols-2 gap-3" data-testid={`lead-timeline-panel-${leadId}`}>
            {/* ── KI-Coach ── */}
            <div className="border border-[#111827]/15 bg-gray-50 rounded-sm flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider rounded-t-sm">
                    <Sparkles className="w-3.5 h-3.5 text-[#FFCC00]" /> KI-Coach — Einschätzung & Einwandsbehandlung
                </div>
                <div ref={coachRef} className="flex-1 max-h-64 overflow-y-auto px-3 py-3 space-y-2" data-testid={`lead-coach-msgs-${leadId}`}>
                    {coachMsgs === null ? (
                        <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lead wird analysiert…</div>
                    ) : coachMsgs.map((m, i) => (
                        <div key={i} className={`text-xs leading-relaxed whitespace-pre-wrap rounded-sm px-3 py-2 max-w-[92%] ${m.role === "user" ? "ml-auto bg-[#FFCC00]/25 border border-[#FFCC00]/60 text-[#111827]" : "bg-white border border-gray-200 text-gray-800"}`}>
                            <Bold text={m.text} />
                            {m.role !== "user" && (
                                <button type="button" onClick={() => speak(m.text, i)}
                                    className={`mt-1.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${speaking === i ? "text-red-600" : "text-gray-400 hover:text-[#111827]"}`}
                                    title="Einschätzung vorlesen — perfekt fürs Telefonieren nebenbei"
                                    data-testid={`lead-coach-speak-${leadId}-${i}`}>
                                    {speaking === `busy-${i}` ? <Loader2 className="w-3 h-3 animate-spin" />
                                        : speaking === i ? <Square className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                                    {speaking === i ? "Stopp" : speaking === `busy-${i}` ? "Lädt…" : "Vorlesen"}
                                </button>
                            )}
                        </div>
                    ))}
                    {coachBusy && coachMsgs !== null && (
                        <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Coach denkt nach…</div>
                    )}
                </div>
                <div className="border-t border-gray-200 px-3 py-2 space-y-2">
                    {!showObjection ? (
                        <button type="button" onClick={() => setShowObjection(true)} disabled={objBusy}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-sm border border-[#111827] bg-white text-[#111827] hover:bg-[#111827] hover:text-[#FFCC00] transition-colors disabled:opacity-50"
                            data-testid={`lead-objection-btn-${leadId}`}>
                            {objBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldQuestion className="w-3.5 h-3.5" />} Einwandsbehandlung — Kunden gewinnen
                        </button>
                    ) : (
                        <div className="space-y-1.5" data-testid={`lead-objection-form-${leadId}`}>
                            <div className="flex flex-wrap gap-1.5">
                                {quickObjections.map((q) => (
                                    <button key={q} type="button" disabled={objBusy} onClick={() => runObjection(q)}
                                        className="text-[11px] font-bold px-2 py-1 rounded-sm border border-gray-300 bg-white hover:border-[#111827] disabled:opacity-50"
                                        data-testid={`lead-objection-quick-${leadId}`}>{q}</button>
                                ))}
                                <button type="button" onClick={() => setShowObjection(false)} className="text-[11px] px-1.5 py-1 text-gray-500 hover:text-[#111827]"><X className="w-3.5 h-3.5" /></button>
                            </div>
                            <div className="flex items-center gap-2">
                                <input value={objText} onChange={(e) => setObjText(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && runObjection(objText.trim())}
                                    placeholder="Einwand des Kunden — oder leer lassen für Analyse des Verlaufs"
                                    className="flex-1 text-xs border border-gray-300 rounded-sm px-2 py-1.5 bg-white focus:border-[#111827] focus:outline-none"
                                    data-testid={`lead-objection-input-${leadId}`} />
                                <button type="button" onClick={() => runObjection(objText.trim())} disabled={objBusy}
                                    className="inline-flex items-center gap-1 bg-[#111827] text-white text-xs font-bold rounded-sm px-3 py-1.5 hover:bg-black disabled:opacity-40"
                                    data-testid={`lead-objection-run-${leadId}`}>
                                    {objBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Analysieren
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-2">
                        <input value={coachInput} onChange={(e) => setCoachInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && askCoach()}
                            placeholder="Frage an den Coach — z. B. „Wie starte ich das Gespräch?“"
                            className="flex-1 text-xs border border-gray-300 rounded-sm px-2 py-1.5 bg-white focus:border-[#111827] focus:outline-none"
                            data-testid={`lead-assistant-input-${leadId}`} />
                        <button type="button" onClick={askCoach} disabled={coachBusy || !coachInput.trim()}
                            className="inline-flex items-center gap-1 bg-[#111827] text-white text-xs font-bold rounded-sm px-3 py-1.5 hover:bg-black disabled:opacity-40"
                            data-testid={`lead-assistant-send-${leadId}`}>
                            <Send className="w-3 h-3" /> Senden
                        </button>
                    </div>
                    {/* Aug-2026 (User-Wunsch „Antwort Direktversand") — KI-Antwortvorschlag prüfen & senden */}
                    {lead?.ai_reply_draft && !draft && (
                        <button type="button"
                            onClick={() => setDraft({ subject: `Re: ${lead.subject || "Ihre Anfrage"}`, body: lead.ai_reply_draft })}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1.5 rounded-sm border border-[#FFCC00] bg-[#FFCC00]/15 text-[#111827] hover:bg-[#FFCC00]/40 transition-colors"
                            data-testid={`lead-ai-reply-load-${leadId}`}>
                            <Mail className="w-3.5 h-3.5" /> KI-Antwortvorschlag prüfen & senden
                        </button>
                    )}
                </div>
                {draft && (
                    <div className="border-t-2 border-[#FFCC00] bg-white px-3 py-2.5 space-y-1.5" data-testid={`lead-email-draft-${leadId}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-wider text-[#111827] inline-flex items-center gap-1"><Mail className="w-3 h-3" /> KI-E-Mail-Entwurf an den Kunden</span>
                            <button type="button" onClick={() => setDraft(null)} className="text-gray-400 hover:text-[#111827]"><X className="w-3.5 h-3.5" /></button>
                        </div>
                        <input value={draft.subject} onChange={(e) => setDraft((p) => ({ ...p, subject: e.target.value }))}
                            className="w-full text-xs font-bold border border-gray-300 rounded-sm px-2 py-1.5 focus:border-[#111827] focus:outline-none"
                            data-testid={`lead-draft-subject-${leadId}`} />
                        <textarea value={draft.body} onChange={(e) => setDraft((p) => ({ ...p, body: e.target.value }))} rows={6}
                            className="w-full text-xs border border-gray-300 rounded-sm px-2 py-1.5 focus:border-[#111827] focus:outline-none resize-y"
                            data-testid={`lead-draft-body-${leadId}`} />
                        <div className="flex items-center gap-2">
                            <button type="button" onClick={sendDraft} disabled={sending}
                                className="inline-flex items-center gap-1.5 bg-[#FFCC00] text-[#111827] text-xs font-bold rounded-sm px-3 py-1.5 hover:brightness-95 disabled:opacity-50"
                                data-testid={`lead-draft-send-${leadId}`}>
                                {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Per E-Mail an Kunden senden
                            </button>
                            <button type="button"
                                onClick={() => { navigator.clipboard.writeText(`${draft.subject}\n\n${draft.body}`); toast.success("Entwurf kopiert"); }}
                                className="inline-flex items-center gap-1 text-[11px] font-bold text-gray-600 hover:text-[#111827]"
                                data-testid={`lead-draft-copy-${leadId}`}>
                                <Copy className="w-3 h-3" /> Kopieren
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* ── Chat-Timeline ── */}
            <div className="border border-[#111827]/15 bg-gray-50 rounded-sm flex flex-col">
                <div className="flex items-center gap-2 px-3 py-2 bg-[#111827] text-white text-xs font-bold uppercase tracking-wider rounded-t-sm">
                    <MessageSquare className="w-3.5 h-3.5 text-[#FFCC00]" /> Verlauf — Kommunikation & Notizen
                </div>
                {/* Aug-2026 (User-Wunsch) — Ursprungsnachricht gehört in den Verlauf */}
                {lead?.raw_text && (
                    <details className="border-b border-gray-200 bg-white px-3 py-2 text-xs" data-testid={`lead-timeline-original-${leadId}`}>
                        <summary className="cursor-pointer font-bold text-gray-600 hover:text-[#111827] select-none inline-flex items-center gap-1">
                            <Mail className="w-3.5 h-3.5" /> Ursprungs-E-Mail ansehen
                        </summary>
                        {(lead.forwarded_by_email || lead.forwarded_by_name) && (
                            <div className="mt-1.5 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid={`lead-timeline-forwarded-${leadId}`}>
                                Weitergeleitet von <b>{lead.forwarded_by_name || lead.forwarded_by_email}</b>{lead.forwarded_by_name && lead.forwarded_by_email ? ` (${lead.forwarded_by_email})` : ""} — die Kundendaten stammen aus der Ursprungsnachricht.
                            </div>
                        )}
                        <OriginalEmailBody leadId={leadId} lead={lead} />
                    </details>
                )}
                <div ref={tlRef} className="flex-1 max-h-72 overflow-y-auto px-3 py-3 space-y-2" data-testid={`lead-timeline-list-${leadId}`}>
                    {items === null ? (
                        <div className="text-xs text-gray-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Verlauf wird geladen…</div>
                    ) : items.length === 0 ? (
                        <div className="text-xs text-gray-500 flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Noch keine Einträge — erste Notiz unten erfassen.</div>
                    ) : (
                        items.map((ev, i) => <TimelineEvent key={ev.id || i} ev={ev} />)
                    )}
                </div>
                <div className="flex items-center gap-2 border-t border-gray-200 px-3 py-2">
                    <input value={note} onChange={(e) => setNote(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && addNote()}
                        placeholder="Notiz erfassen — z. B. „Kunde erreicht, Probefahrt Sa 10 Uhr“"
                        className="flex-1 text-xs border border-gray-300 rounded-sm px-2 py-1.5 bg-white focus:border-[#111827] focus:outline-none"
                        data-testid={`lead-note-input-${leadId}`} />
                    <input type="date" value={followUp} onChange={(e) => saveFollowUp(e.target.value)}
                        className="text-xs border border-gray-300 rounded-sm px-2 py-1 bg-white focus:border-[#111827] focus:outline-none"
                        title="Wiedervorlage — wird automatisch gespeichert"
                        data-testid={`lead-followup-input-${leadId}`} />
                    <button type="button" onClick={addNote} disabled={noteBusy || !note.trim()}
                        className="inline-flex items-center gap-1 bg-[#111827] text-white text-xs font-bold rounded-sm px-3 py-1.5 hover:bg-black disabled:opacity-40"
                        data-testid={`lead-note-send-${leadId}`}>
                        <StickyNote className="w-3 h-3" /> Notiz
                    </button>
                </div>
                {/* Aug-2026 (User-Wunsch) — Lead-Aktionen & Zuweisung unter der Notiz-Zeile */}
                {footerActions && (
                    <div className="flex flex-wrap items-center gap-1.5 border-t border-gray-200 px-3 py-2 bg-gray-50/70 rounded-b-sm" data-testid={`lead-footer-actions-${leadId}`}>
                        {footerActions}
                    </div>
                )}
            </div>
        </div>
    );
}
